/**
 * Multiplayer Ludo Service
 *
 * Extends GenericMultiplayerService for online play.
 * Game type: 'ludo'
 * Server generates dice rolls; validates moves server-side.
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
  GameResult,
} from '../../services/multiplayer/GenericMultiplayerService';
import {
  LudoGameState,
  LudoMove,
  MoveResult,
  DiceResult,
  PlayerColor,
  TurnSkipInfo,
} from '../types';

export class MultiplayerLudoService extends GenericMultiplayerService<
  LudoGameState,
  LudoMove
> {
  private onDiceResultCallback?: (result: DiceResult) => void;
  private onMoveResultCallback?: (result: MoveResult) => void;
  private onTurnSkippedCallback?: (info: TurnSkipInfo) => void;
  private onLudoGameStartedCallback?: (state: LudoGameState) => void;
  private onLudoPlayerDisconnectedCallback?: (player: PlayerColor) => void;
  private onLudoPlayerReconnectedCallback?: (player: PlayerColor) => void;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 3;

  constructor(wsService: WebSocketService) {
    super(wsService, 'ludo');
  }

  // =========================================================================
  // Abstract method implementations
  // =========================================================================

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      const { action } = message;

      if (!action || !action.startsWith('ludo:')) return;

      const gameAction = action.replace('ludo:', '');

      switch (gameAction) {
        case 'roomCreated':
          this.roomCode = message.roomCode;
          this.playerRole = message.playerRole || 'player1';
          this.playerColor = message.playerColor || 'red';
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'roomJoined':
          this.roomCode = message.roomCode;
          this.playerRole = message.playerRole || 'player2';
          this.playerColor = message.playerColor || 'blue';
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'opponentJoined':
          if (this.onPlayerConnectedCallback) {
            this.onPlayerConnectedCallback(message.playerId || 'opponent');
          }
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'gameStarted':
          if (message.gameState) {
            this.gameState = message.gameState;
            if (this.onLudoGameStartedCallback) {
              this.onLudoGameStartedCallback(message.gameState);
            }
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
          }
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'diceResult':
          if (this.onDiceResultCallback) {
            this.onDiceResultCallback({
              value: message.value,
              player: message.player,
              consecutiveSixes: message.consecutiveSixes || 0,
              isTripleSix: message.isTripleSix || false,
            });
          }
          break;

        case 'moveResult':
          if (message.move && this.onMoveResultCallback) {
            this.onMoveResultCallback(message as MoveResult);
          }
          if (message.gameState) {
            this.gameState = message.gameState;
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
          }
          break;

        case 'turnSkipped':
          if (this.onTurnSkippedCallback) {
            this.onTurnSkippedCallback({
              player: message.player,
              reason: message.reason,
              nextTurn: message.nextTurn,
            });
          }
          break;

        case 'gameOver':
          if (this.onGameOverCallback) {
            this.onGameOverCallback({
              result: message.result || 'win',
              winner: message.winner,
              reason: message.reason,
            });
          }
          break;

        case 'gameState':
          if (message.gameState) {
            this.gameState = message.gameState;
            this.reconnectAttempts = 0;
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
          }
          break;

        case 'playerDisconnected':
          if (this.onLudoPlayerDisconnectedCallback) {
            this.onLudoPlayerDisconnectedCallback(message.player);
          }
          if (this.onPlayerDisconnectedCallback) {
            this.onPlayerDisconnectedCallback(message.playerId || message.player);
          }
          break;

        case 'playerReconnected':
          if (this.onLudoPlayerReconnectedCallback) {
            this.onLudoPlayerReconnectedCallback(message.player);
          }
          if (this.onPlayerConnectedCallback) {
            this.onPlayerConnectedCallback(message.playerId || message.player);
          }
          break;

        case 'error':
          this.handleError(message.message || 'Unknown multiplayer error');
          break;

        default:
          console.warn(`Unknown ludo action: ${gameAction}`);
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({
      action: 'ludo:createRoom',
      playerId: this.playerId,
      gameType: this.gameType,
    });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({
      action: 'ludo:joinRoom',
      roomCode,
      playerId: this.playerId,
      gameType: this.gameType,
    });
  }

  protected sendLeaveRoomMessage(): void {
    this.wsService.send({
      action: 'ludo:resign',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  protected getMaxPlayers(): number {
    return 4;
  }

  // =========================================================================
  // Game-specific methods
  // =========================================================================

  async sendMove(move: LudoMove): Promise<boolean> {
    try {
      this.wsService.send({
        action: 'ludo:moveToken',
        roomCode: this.roomCode,
        playerId: this.playerId,
        tokenIndex: move.tokenIndex,
        moveAction: move.action,
      });
      return true;
    } catch (error) {
      this.handleError('Failed to send move', error);
      return false;
    }
  }

  requestDiceRoll(): void {
    this.wsService.send({
      action: 'ludo:rollDice',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  isMyTurn(currentTurn: string): boolean {
    return this.playerColor === currentTurn || this.playerRole === currentTurn || this.playerId === currentTurn;
  }

  async resign(): Promise<void> {
    this.wsService.send({
      action: 'ludo:resign',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  getPlayerColorTyped(): PlayerColor | null {
    return this.playerColor as PlayerColor | null;
  }

  // =========================================================================
  // Ludo-specific event callbacks
  // =========================================================================

  onLudoGameStarted(callback: (state: LudoGameState) => void): void {
    this.onLudoGameStartedCallback = callback;
  }

  onDiceResult(callback: (result: DiceResult) => void): void {
    this.onDiceResultCallback = callback;
  }

  onMoveResult(callback: (result: MoveResult) => void): void {
    this.onMoveResultCallback = callback;
  }

  onTurnSkipped(callback: (info: TurnSkipInfo) => void): void {
    this.onTurnSkippedCallback = callback;
  }

  onLudoPlayerDisconnected(callback: (player: PlayerColor) => void): void {
    this.onLudoPlayerDisconnectedCallback = callback;
  }

  onLudoPlayerReconnected(callback: (player: PlayerColor) => void): void {
    this.onLudoPlayerReconnectedCallback = callback;
  }

  // =========================================================================
  // Connection management
  // =========================================================================

  handleDisconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.pow(2, this.reconnectAttempts - 1) * 1000; // 1s, 2s, 4s
      setTimeout(() => {
        this.reconnect();
      }, delay);
    }
  }

  reconnect(): void {
    if (this.roomCode) {
      this.wsService.send({
        action: 'ludo:reconnect',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });
    }
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }
}
